import { isEmoji } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import { WEBSITE_DEFAULTS, pickText } from './headerDefaults';
import { CUSTOM_ROW_ICON, DESTINATION_ROW_ICON, holidayTypeIcon } from './headerIcons';
import { customMenuKey } from './siteHeaderModel';

// What each dropdown menu will list on the website with the settings as they stand right now,
// unsaved edits included. The editor cards use it for their "Website shows" line and the live
// preview draws it. Rows: [{ key, label, sub, icon, builtIn, url }], where `icon` is a chosen
// emoji ('' for none) and `builtIn` the website's own icon for a row left on Automatic.

export const isActiveType = (t) => Boolean(t) && (t.active === true || Number(t.active) === 1);

// Mirrors the homepage editor's preview of a popular-destination link: the search it opens.
export function destinationLinkPreview(item) {
  if (item?.url) return item.url;
  const qs = new URLSearchParams();
  if (item?.dest?.code) qs.set(item.dest.type === 'country' ? 'countries' : 'destinations', item.dest.code);
  if (item?.boardCode) qs.set('boards', item.boardCode);
  if (item?.holidayTypeId) qs.set('themes', String(item.holidayTypeId));
  if ([...qs.keys()].length === 0) return null;
  return `/results?${qs.toString()}`;
}

const homepageLinkLabel = (link) =>
  typeof link === 'string' ? link : link?.label || link?.dest?.name || '';

const homepageLinkOpens = (link) =>
  Boolean(link && typeof link === 'object' && (link.dest?.code || link.boardCode || link.holidayTypeId));

/**
 * The Destinations menu. From the homepage it is the popular-destination groups' links in
 * their order, de-duplicated by label, unlinked labels left out; hand-picked it is the active
 * rows. Either way capped at maxItems.
 */
export function destinationRows(menu, homepageGroups, lang) {
  if (!menu) return [];
  if (menu.source === 'manual') {
    return (menu.items || [])
      .filter((it) => it.active)
      .slice(0, menu.maxItems)
      .map((it) => ({
        key: it.id,
        label: pickText(it.label, lang, it.dest?.name || null),
        sub: pickText(it.sub, lang),
        icon: isEmoji(it.icon) ? it.icon : '',
        builtIn: DESTINATION_ROW_ICON,
        url: destinationLinkPreview(it),
      }));
  }
  const seen = new Set();
  const out = [];
  for (const g of Array.isArray(homepageGroups) ? homepageGroups : []) {
    for (const link of Array.isArray(g?.links) ? g.links : []) {
      const label = homepageLinkLabel(link);
      if (!label || !homepageLinkOpens(link) || seen.has(label)) continue;
      seen.add(label);
      out.push({
        key: `${label}-${out.length}`,
        label,
        sub: menu.showGroupAsSub ? String(g?.title || '') : '',
        icon: '',
        builtIn: DESTINATION_ROW_ICON,
        url: destinationLinkPreview(link),
      });
      if (out.length >= menu.maxItems) return out;
    }
  }
  return out;
}

/**
 * The Holiday types menu, resolved the way the server resolves `resolved`: active types only,
 * the hand-picked order or the Holiday Types order, capped at maxItems.
 */
export function holidayTypeRows(menu, types, lang) {
  if (!menu) return [];
  const live = (Array.isArray(types) ? types : []).filter(isActiveType);
  const defaultSub = menu.sub;
  const shape = (t, item) => ({
    key: String(t.id),
    id: Number(t.id),
    label: pickText(item?.label, lang, t.name),
    sub: pickText(item?.sub, lang, pickText(defaultSub, lang, WEBSITE_DEFAULTS.exploreHolidays)),
    icon: isEmoji(item?.icon) ? item.icon : '',
    builtIn: holidayTypeIcon(t.name),
    url: `/holidays/${t.slug || String(t.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  });
  if (menu.source === 'manual') {
    const byId = new Map(live.map((t) => [Number(t.id), t]));
    return (menu.items || [])
      .map((it) => {
        const t = byId.get(Number(it.holidayTypeId));
        return t ? shape(t, it) : null;
      })
      .filter(Boolean)
      .slice(0, menu.maxItems);
  }
  return live.slice(0, menu.maxItems).map((t) => shape(t));
}

/** A custom menu: its active rows that have a label and a link. */
export function customRows(menu, lang) {
  return (menu?.items || [])
    .filter((it) => it.active && String(it.url || '').trim() && (it.label?.nl || it.label?.en))
    .map((it) => ({
      key: it.id,
      label: pickText(it.label, lang),
      sub: pickText(it.sub, lang),
      icon: isEmoji(it.icon) ? it.icon : '',
      builtIn: CUSTOM_ROW_ICON,
      url: it.url,
    }));
}

/**
 * Every menu in menuOrder with what it would draw: [{ key, kind, menu, label, rows, footer }].
 * `footer` is the "see all" row's text, or null.
 */
export function previewMenus(sh, { homepageGroups, holidayTypes, lang }) {
  const custom = new Map((sh.menus.custom || []).map((m) => [customMenuKey(m.id), m]));
  return (sh.menuOrder || [])
    .map((key) => {
      let kind;
      let menu;
      let rows;
      let fallback = null;
      if (key === 'destinations') {
        kind = 'destinations';
        menu = sh.menus.destinations;
        rows = destinationRows(menu, homepageGroups, lang);
        fallback = WEBSITE_DEFAULTS.destinationsLabel;
      } else if (key === 'holidayTypes') {
        kind = 'holidayTypes';
        menu = sh.menus.holidayTypes;
        rows = holidayTypeRows(menu, holidayTypes, lang);
        fallback = WEBSITE_DEFAULTS.holidayTypesLabel;
      } else {
        kind = 'custom';
        menu = custom.get(key);
        if (!menu) return null;
        rows = customRows(menu, lang);
      }
      const f = menu.footerLink;
      return {
        key,
        kind,
        menu,
        label: pickText(menu.label, lang, fallback),
        rows,
        footer: f?.enabled && f.url ? pickText(f.label, lang, WEBSITE_DEFAULTS.seeAll) : null,
      };
    })
    .filter(Boolean);
}

/** Whether a block with this showOn appears on the given device. */
export const showsOn = (showOn, device) => showOn === 'all' || !showOn || showOn === device;
